import type { BankReconAdjustmentType, BankStatementListRow } from '@dcc-erp/shared';

export type ReconTab = 'overview' | 'match' | 'statement' | 'history';
export type ReconAccountStatus = 'no-statement' | 'reconciled' | 'has-open';
export type AdjustmentDir = 'gain' | 'loss';

// Prisma Decimal columns arrive as strings ("0.00") on the wire — coerce before math.
export function num(v: string | number | null | undefined): number {
  if (v === null || v === undefined) return 0;
  return typeof v === 'number' ? v : Number(v);
}

// "statement ปัจจุบัน" ของแต่ละบัญชี = ตัวที่ statementDate ใหม่สุด
export function latestStatementByAccount(
  statements: BankStatementListRow[] | undefined,
): Map<string, BankStatementListRow> {
  const map = new Map<string, BankStatementListRow>();
  for (const s of statements ?? []) {
    const prev = map.get(s.bankAccountId);
    if (!prev || new Date(s.statementDate).getTime() > new Date(prev.statementDate).getTime()) {
      map.set(s.bankAccountId, s);
    }
  }
  return map;
}

export function accountStatus(s: BankStatementListRow | undefined): ReconAccountStatus {
  if (!s) return 'no-statement';
  return s.status === 'RECONCILED' ? 'reconciled' : 'has-open';
}

export function lineMovement(lines: { debit: string | number; credit: string | number }[]): number {
  return lines.reduce((sum, l) => sum + num(l.credit) - num(l.debit), 0);
}

// ผู้ใช้กรอกจำนวนเป็นบวกเสมอ — โค้ดแปลงเครื่องหมายตามประเภท (กัน JV ลงผิดทิศ).
// อ้างอิง bank-recon.service.ts: BANK_FEE amount<0, INTEREST amount>0, FX signed.
export function signedAdjustmentAmount(
  type: BankReconAdjustmentType,
  magnitude: number,
  dir: AdjustmentDir = 'gain',
): number {
  const m = Math.abs(magnitude);
  switch (type) {
    case 'BANK_FEE':
    case 'CHECK_NOT_CLEARED':
      return -m;
    case 'INTEREST':
    case 'DEPOSIT_IN_TRANSIT':
      return m;
    case 'FX':
    case 'OTHER':
      return dir === 'loss' ? -m : m;
  }
}

// ---- CSV/XLSX import template ----
// หัวคอลัมน์คงที่ (เทมเพลตเดียวมาตรฐาน — ไม่ auto-detect รูปแบบของแต่ละธนาคาร)
export const STATEMENT_TEMPLATE_HEADERS = [
  'วันที่',
  'รายละเอียด',
  'เงินออก',
  'เงินเข้า',
  'อ้างอิง',
  'คู่ค้า',
] as const;

export interface ParsedStatementLine {
  date: string;
  description: string;
  debit?: number;
  credit?: number;
  ref?: string;
  party?: string;
}

// แปลง grid (จาก fileToGrid) → lines + errors. คาดหวัง row[0] = header.
export function parseStatementGrid(grid: string[][]): {
  lines: ParsedStatementLine[];
  errors: string[];
} {
  const errors: string[] = [];
  const header = (grid[0] ?? []).map((h) => h.trim());
  const idx = (name: string) => header.indexOf(name);
  const iDate = idx('วันที่');
  const iDesc = idx('รายละเอียด');
  const iDebit = idx('เงินออก');
  const iCredit = idx('เงินเข้า');
  if (iDate === -1 || iDesc === -1 || iDebit === -1 || iCredit === -1) {
    errors.push('หัวคอลัมน์ไม่ครบ — ต้องมี: ' + STATEMENT_TEMPLATE_HEADERS.join(' | '));
    return { lines: [], errors };
  }
  const iRef = idx('อ้างอิง');
  const iParty = idx('คู่ค้า');
  const toNum = (s: string) => Number((s ?? '').replace(/,/g, '').trim()) || 0;

  const lines: ParsedStatementLine[] = [];
  for (let r = 1; r < grid.length; r += 1) {
    const cols = grid[r] ?? [];
    if (cols.every((c) => (c ?? '').trim() === '')) continue; // ข้ามแถวว่าง
    const date = (cols[iDate] ?? '').trim();
    const description = (cols[iDesc] ?? '').trim();
    const debit = toNum(cols[iDebit] ?? '');
    const credit = toNum(cols[iCredit] ?? '');
    if (!date || !description) {
      errors.push(`แถว ${r}: ต้องมีวันที่และรายละเอียด`);
      continue;
    }
    if (!(debit > 0) && !(credit > 0)) {
      errors.push(`แถว ${r}: ต้องมีเงินออกหรือเงินเข้าอย่างใดอย่างหนึ่ง (> 0)`);
      continue;
    }
    if (debit > 0 && credit > 0) {
      errors.push(`แถว ${r}: มีทั้งเงินออกและเงินเข้า — ใส่ได้ด้านเดียว`);
      continue;
    }
    const line: ParsedStatementLine = { date, description };
    if (debit > 0) line.debit = debit;
    if (credit > 0) line.credit = credit;
    const ref = iRef === -1 ? '' : (cols[iRef] ?? '').trim();
    const party = iParty === -1 ? '' : (cols[iParty] ?? '').trim();
    if (ref) line.ref = ref;
    if (party) line.party = party;
    lines.push(line);
  }
  return { lines, errors };
}
